import { useState, useEffect } from 'react';
import AdminSidebar from '@/components/AdminSidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Newspaper, 
  Image, 
  Package, 
  Users, 
  TrendingUp,
  Eye,
  Plus,
  Database,
  BarChart3,
  Activity,
  AlertCircle,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db, COLLECTIONS, trackPageView, trackUserSession } from '@/lib/firebase';
import { getGoogleAnalyticsData, openFirebaseAnalytics, openGoogleAnalytics } from '@/lib/google-analytics';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    news: 0,
    visitors: '--',
    pageViews: '--',
    sessions: '--',
    bounceRate: '--'
  });

  const [loading, setLoading] = useState(true);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setAnalyticsError(null);
        
        // Récupérer les stats des actualités
        const newsSnapshot = await getDocs(collection(db, COLLECTIONS.NEWS));
        
        // Récupérer les stats Google Analytics
        const analyticsStats = await getGoogleAnalyticsData();
        
        console.log('📊 Statistiques récupérées:', analyticsStats);
        
        if (analyticsStats.error) {
          setAnalyticsError(analyticsStats.error);
          setStats({
            news: newsSnapshot.size,
            visitors: '0',
            pageViews: '0',
            sessions: '0',
            bounceRate: '0%'
          });
        } else {
          setStats({
            news: newsSnapshot.size,
            visitors: analyticsStats.users.toString(),
            pageViews: analyticsStats.pageViews.toString(),
            sessions: analyticsStats.sessions.toString(),
            bounceRate: `${analyticsStats.bounceRate.toFixed(1)}%`
          });
        }
      } catch (error) {
        console.error('Erreur lors du chargement des statistiques:', error);
        setAnalyticsError('Erreur de chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    
    // Tracker la visite de la page dashboard
    trackPageView('admin_dashboard');
    trackUserSession();
  }, []);

  const quickActions = [
    {
      title: 'Ajouter une actualité',
      description: 'Créer un nouvel article',
      icon: Plus,
      href: '/admin/news/new',
      color: 'bg-blue-500'
    },
    {
      title: 'Firebase Analytics',
      description: 'Voir les vraies statistiques',
      icon: BarChart3,
      action: openFirebaseAnalytics,
      color: 'bg-purple-500',
      external: true
    }
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      <AdminSidebar />
      
      <div className="flex-1 overflow-auto">
        <div className="p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">Vue d'ensemble de votre site</p>
          </div>

          {/* Message d'erreur Analytics */}
          {analyticsError && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Statistiques Analytics
                  </h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    {analyticsError}
                  </p>
                  <div className="mt-3 flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={openFirebaseAnalytics}
                      className="text-xs"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Firebase Analytics
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={openGoogleAnalytics}
                      className="text-xs"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Google Analytics
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Actualités</CardTitle>
                <Newspaper className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{loading ? '...' : stats.news}</div>
                <p className="text-xs text-muted-foreground">Articles publiés</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Visiteurs</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{loading ? '...' : stats.visitors}</div>
                <p className="text-xs text-muted-foreground">Utilisateurs uniques</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pages vues</CardTitle>
                <Eye className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{loading ? '...' : stats.pageViews}</div>
                <p className="text-xs text-muted-foreground">Vues de pages</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Sessions</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{loading ? '...' : stats.sessions}</div>
                <p className="text-xs text-muted-foreground">Sessions actives</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Taux de rebond</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{loading ? '...' : stats.bounceRate}</div>
                <p className="text-xs text-muted-foreground">Taux de rebond</p>
              </CardContent>
            </Card>
          </div>

          {/* Actions rapides */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Actions rapides</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardHeader>
                      <div className="flex items-center space-x-2">
                        <div className={`p-2 rounded-lg ${action.color}`}>
                          <Icon className="h-4 w-4 text-white" />
                        </div>
                        <CardTitle className="text-sm">{action.title}</CardTitle>
                      </div>
                      <CardDescription>{action.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {action.external ? (
                        <Button 
                          variant="outline" 
                          className="w-full"
                          onClick={action.action}
                        >
                          Accéder
                        </Button>
                      ) : (
                        <Link to={action.href}>
                          <Button variant="outline" className="w-full">
                            Accéder
                          </Button>
                        </Link>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Liens rapides */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Gestion du contenu</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link to="/admin/news">
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardHeader>
                    <div className="flex items-center space-x-2">
                      <Newspaper className="h-5 w-5 text-blue-500" />
                      <CardTitle className="text-sm">Gérer les actualités</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      Modifier, supprimer et organiser vos articles
                    </p>
                  </CardContent>
                </Card>
              </Link>

              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={openFirebaseAnalytics}>
                <CardHeader>
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="h-5 w-5 text-purple-500" />
                    <CardTitle className="text-sm">Statistiques détaillées</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600">
                    Voir les statistiques complètes dans Firebase Analytics
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 